// Demo Arena — the public demo's dummy venue (spec/24 §2.1).
//
// One organisation, one venue, eight PCs in two classes and two zones, ten players, a pass shop and a small
// menu. The same for every visitor, and never stored anywhere but the visitor's browser.
//
// **Ids.** The organisation, the venue and the players carry the same ids as the console's in-memory api
// (`apps/console/src/testing/fake-server.ts`, `fake-venue-players.ts`), because the demo console answers the
// screens this package does not model — settings, staff, a player's wallet statement, reports — from those
// fixtures: one venue, whichever screen shows it. A console test holds the two lists together. Everything the
// simulation owns (classes, zones, stations, passes, the menu) has ids of its own, from `demoId` below.
//
// **Money** is integer paise in `INR`, like every amount (spec/06 §2). **Contacts** are fake: every address is
// on `example.com` (RFC 2606) and nothing in the demo sends anything to anyone (spec/24 §6).
/** The 23 characters every demo-owned id shares, before its 3-character tail: a valid TypeID suffix is 26. */
const DEMO_ID_BODY = '01k6d3m0a7f2r9x4c8vn5bq';
/** A TypeID with the demo's own body: `prefix_01k6d3m0a7f2r9x4c8vn5bq` + a three-character tail. */
export function demoId(prefix, tail) {
    if (!/^[0-9a-hjkmnp-tv-z]{3}$/.test(tail))
        throw new TypeError(`demoId: "${tail}" is not three base32 characters.`);
    return `${prefix}_${DEMO_ID_BODY}${tail}`;
}
export const DEMO_CURRENCY = 'INR';
/** `ORGANIZATION.id` in the console's fake api. */
export const DEMO_ORGANIZATION = { id: 'org_01k5znw4hmfay8vvr14d2pf2db', name: 'Demo Gaming Co.', slug: 'playground' };
/** `VENUE_ID` in the console's fake api, which the demo console names Demo Arena (`/v/demo-arena/`). */
export const DEMO_VENUE = {
    id: 'ven_01k5znw4hmfay8vvr14d2pf2dc',
    name: 'Demo Arena',
    slug: 'demo-arena',
    locality: 'Indiranagar, Bengaluru',
    timezone: 'Asia/Kolkata',
};
export const DEMO_CLASSES = [
    {
        id: demoId('scl', '0c1'),
        name: 'Standard PC',
        colorToken: 'category-1',
        description: 'RTX 4060 PCs at 165 Hz — the everyday floor.',
        hourlyRate: 6_000,
    },
    {
        id: demoId('scl', '0c2'),
        name: 'Pro Arena',
        colorToken: 'category-3',
        description: 'RTX 4080 rigs at 360 Hz with pro peripherals.',
        hourlyRate: 12_000,
    },
];
const [STANDARD, PRO] = DEMO_CLASSES;
export const DEMO_ZONES = [
    { id: demoId('zon', '0z1'), name: 'Main hall', colorToken: 'category-2' },
    { id: demoId('zon', '0z2'), name: 'Pro corner', colorToken: 'category-4' },
];
const [MAIN_HALL, PRO_CORNER] = DEMO_ZONES;
const station = (number, stationClass, zone) => ({
    id: demoId('stn', `0s${number}`),
    number,
    name: `PC ${String(number).padStart(2, '0')}`,
    classId: stationClass.id,
    zoneId: zone.id,
});
/** Eight PCs: six Standard in the main hall, two Pro in the corner. */
export const DEMO_STATIONS = [
    station(1, STANDARD, MAIN_HALL),
    station(2, STANDARD, MAIN_HALL),
    station(3, STANDARD, MAIN_HALL),
    station(4, STANDARD, MAIN_HALL),
    station(5, STANDARD, MAIN_HALL),
    station(6, STANDARD, MAIN_HALL),
    station(7, PRO, PRO_CORNER),
    station(8, PRO, PRO_CORNER),
];
export const DEMO_PASSES = [
    { id: demoId('ppr', '0p1'), name: '15 minutes', seconds: 15 * 60, price: 2_000, classIds: [] },
    { id: demoId('ppr', '0p2'), name: '1 hour', seconds: 3_600, price: 6_000, classIds: [STANDARD.id] },
    { id: demoId('ppr', '0p3'), name: '3 hours', seconds: 3 * 3_600, price: 15_000, classIds: [STANDARD.id] },
    { id: demoId('ppr', '0p4'), name: 'Pro hour', seconds: 3_600, price: 12_000, classIds: [PRO.id] },
];
/**
 * The console fake's ten players, by its ids. Usernames are what the kiosk signs them in with; the fake's own
 * usernames are kept where it has one, and the rest given one, lower case.
 */
export const DEMO_PLAYERS = [
    { id: 'ply_01k5znw4hmfay8vvr14d2pf2a1', username: 'aarav', firstName: 'Aarav', lastName: 'Mehta', wallet: 42_000, pass: null },
    { id: 'ply_01k5znw4hmfay8vvr14d2pf5b1', username: 'diyarao', firstName: 'Diya', lastName: 'Rao', wallet: 99_000, pass: { name: 'Quick 5 minutes', seconds: 5 * 60 } },
    { id: 'ply_01k5znw4hmfay8vvr14d2pf5b2', username: 'kabir_s', firstName: 'Kabir', lastName: 'Singh', wallet: 0, pass: null },
    { id: 'ply_01k5znw4hmfay8vvr14d2pf5b3', username: 'ananya_i', firstName: 'Ananya', lastName: 'Iyer', wallet: 35_000, pass: { name: '3 hours', seconds: 3 * 3_600 } },
    { id: 'ply_01k5znw4hmfay8vvr14d2pf5b4', username: 'rohang', firstName: 'Rohan', lastName: 'Gupta', wallet: 64_000, pass: { name: '2 hours', seconds: 2 * 3_600 } },
    { id: 'ply_01k5znw4hmfay8vvr14d2pf5b5', username: 'ishaan', firstName: 'Ishaan', lastName: 'Nair', wallet: 18_000, pass: null },
    { id: 'ply_01k5znw4hmfay8vvr14d2pf5b6', username: 'meera', firstName: 'Meera', lastName: 'Krishnan', wallet: 25_000, pass: null },
    { id: 'ply_01k5znw4hmfay8vvr14d2pf5b7', username: 'vrx', firstName: 'Vihaan', lastName: 'Reddy', wallet: 150_000, pass: { name: '2 hours', seconds: 2 * 3_600 } },
    { id: 'ply_01k5znw4hmfay8vvr14d2pf5b8', username: 'saanvi.j', firstName: 'Saanvi', lastName: 'Joshi', wallet: 52_000, pass: null },
    { id: 'ply_01k5znw4hmfay8vvr14d2pf5b9', username: 'arjun', firstName: 'Arjun', lastName: 'Banerjee', wallet: 30_000, pass: null },
];
/** The players the demo's own tips suggest signing in as at the kiosk (spec/24 §4.4). */
export const DEMO_SUGGESTED_PLAYERS = {
    /** Holds a five-minute pass: their session reaches its warning, grace and end inside the demo. */
    quick: 'diyarao',
    /** Holds two hours: a long session to explore the kiosk with. */
    long: 'vrx',
};
/** What every demo player signs in with at the demo kiosk. Not a credential: nothing on the server knows it. */
export const DEMO_PLAYER_PASSWORD = 'demo';
export const DEMO_MENU_CATEGORIES = [
    { id: demoId('mct', '0m1'), name: 'Snacks' },
    { id: demoId('mct', '0m2'), name: 'Drinks' },
];
const [SNACKS, DRINKS] = DEMO_MENU_CATEGORIES;
export const DEMO_MENU_PRODUCTS = [
    { id: demoId('prd', '0f1'), categoryId: SNACKS.id, name: 'Masala fries', description: 'Crisp fries with peri-peri masala.', price: 12_000 },
    { id: demoId('prd', '0f2'), categoryId: SNACKS.id, name: 'Paneer tikka roll', description: 'Smoky paneer, onions and mint chutney.', price: 18_000 },
    { id: demoId('prd', '0f3'), categoryId: SNACKS.id, name: 'Chicken momos', description: 'Eight steamed momos with red chutney.', price: 16_000 },
    { id: demoId('prd', '0f4'), categoryId: DRINKS.id, name: 'Cold coffee', description: 'Blended, with a scoop of ice cream.', price: 11_000 },
    { id: demoId('prd', '0f5'), categoryId: DRINKS.id, name: 'Fresh lime soda', description: 'Sweet, salted or mixed.', price: 7_000 },
    { id: demoId('prd', '0f6'), categoryId: DRINKS.id, name: 'Energy drink', description: 'A 250 ml can, chilled.', price: 13_000 },
];
/** How long a session stays in grace after its time runs out, in the demo (spec/24 §2.2): two minutes. */
export const DEMO_GRACE_SECONDS = 120;
/** The low-time warnings, in minutes left (spec/05 §5), as the demo fires them. */
export const DEMO_WARNING_MINUTES = [10, 5, 1];
/** "Ending soon" on the floor: ten minutes or less (spec/13 §3). */
export const DEMO_ENDING_SOON_SECONDS = 600;
export function stationById(id) {
    return DEMO_STATIONS.find((candidate) => candidate.id === id);
}
export function classById(id) {
    return DEMO_CLASSES.find((candidate) => candidate.id === id);
}
export function playerById(id) {
    return DEMO_PLAYERS.find((candidate) => candidate.id === id);
}
export function productById(id) {
    return DEMO_MENU_PRODUCTS.find((candidate) => candidate.id === id);
}
export function passById(id) {
    return DEMO_PASSES.find((candidate) => candidate.id === id);
}
/** A demo player by the gamer tag they sign in with, ignoring case. */
export function playerByUsername(username) {
    const wanted = username.trim().toLowerCase().replace(/^@/, '');
    return DEMO_PLAYERS.find((candidate) => candidate.username === wanted);
}
export function playerDisplayName(player) {
    return `${player.firstName} ${player.lastName}`;
}
//# sourceMappingURL=venue.js.map